import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs } from "../../../Styles";
import { MainContentPaddingTypes, NewInputList, NewModal, NewSelectList, Overlay, WIDGET_BAR } from "../..";
import { ButtonTypes, NewInputListHandles, NewSelectListHandles, WidgetPlacements } from "../../../Definitions";
import { Graph_QL, System, Widget, useWidget } from "../../../Services";
import NewButton, { NewButtonList } from "../../BaseComponents/Buttons/newButton";

// #region | WidgetList
const WidgetList = memo(({ widgets, onCheck, selectedWidgetIds, deletable, editable, onChange }: {
  widgets: Widget[],
  onCheck: (widgetId: number) => void,
  onChange: (widget: Widget) => void,
  selectedWidgetIds: number[],
  deletable: boolean,
  editable: boolean
}) => (
  <>
    <WIDGET_BAR headerBar borderRadius={{borderRadius: widgets.length === 0 ? "10px" : "10px 10px 0px 0px"}}/> 
    {widgets.map((w, index) => {
      const widgetId = w.widget_id
      const isSelected = selectedWidgetIds.includes(widgetId);

      return (
        <WIDGET_BAR
          key={w.widget_id}
          widget={w}
          editable={editable}
          deletable={deletable}
          borderRadius={{ borderRadius: index === widgets.length - 1 ? "0px 0px 10px 10px" : "0px" }}
          onCheck={() => onCheck(widgetId)}
          isChecked={isSelected}
          onChange={onChange}
        />
      )
    })}
  </>
))
// #endregion

type WidgetModal_Props = {
  system?: System
  placement: WidgetPlacements
  isModalOpen: boolean
  widgets: Widget[]
  closeModal: () => void
  connect: {
    domain: string,
    connection: Graph_QL.Connection;
  }
};
/**
 * WidgetModal
 * @param system -
 * @param isModalOpen -
 * @param closeModal -
 * @param connect - 
 * @param connect.farm -
 * @param connect.domain -
 * @param connect.connection - 
 * @returns 
 */
export default function WidgetModal({ isModalOpen=false, widgets, system, closeModal, connect, placement}:WidgetModal_Props) {
  const {t} = useTranslation()
  const { createWidget } = useWidget(connect.connection);
  const [currentView, setCurrentView] = useState<"ADD" | "MANAGE">("ADD");
  const [addType, setAddType] = useState<"CREATE" | "EXCISTING">("CREATE");
  const [mode, setMode] = useState<"BASIC" | "EDIT" | "DELETE">("BASIC")
  const [message, setMessage] = useState<string | null>(null)
  const [selectedWidgetIds, setSelectedWidgetIds] = useState<number[]>([]);

  // Data
  const { deleteWidget, maintainEntries: updateWidget } = useWidget(connect.connection)
  
// const allWidgets = useMemo(() => widgets, [widgets]);

  const widgetsToShow = useMemo(() => {
    switch (placement) {
      case WidgetPlacements.dashboard:
        return widgets.filter(w => w.placement === 0);
      case WidgetPlacements.kpi:
        return widgets.filter(w => w.placement === 1);
      case WidgetPlacements.report:
        return widgets.filter(w => w.placement === 2);
      case WidgetPlacements.system:
        return widgets.filter(w => w.placement === 3 && w.page === system?.system_id);
      default:
        return [];
    }
  }, [widgets, placement, system?.system_id]);

  const placementLabel = (placement: number, page: number) => {
    switch (placement) {
      case 0: return "Dashboard";
      case 1: return "Kpi";
      case 2: return "Reports";
      case 3:
        if (system && system.system_id === page) {
          return system.name;
        }
        return "System";
      default:
        return "Unknown Placement";
    }
  };

  // #region | Delete widget
  const singleCheck = useCallback((widgetId: number) => {
    setSelectedWidgetIds((prev) =>
      prev.includes(widgetId) ? prev.filter((id) => id !== widgetId) : [...prev, widgetId]
    );
  }, [])
  
  const handleDelete = () => {
    if (!deleteWidget) return
  
    const widgetsToDelete = widgets.filter((w) =>
      selectedWidgetIds.includes(w.widget_id)
    )
  
    deleteWidget({data: widgetsToDelete});
  }
  // #endregion

  // #region | Create widget based on addType
  const createInputRefList = useRef<NewInputListHandles>(null);
  const createSelectRefList = useRef<NewSelectListHandles>(null);
  const excistingSelectRefList = useRef<NewSelectListHandles>(null);

  const handleCreate = () => {
    if (addType === "CREATE") {
      if (createInputRefList && createInputRefList.current && createSelectRefList && createSelectRefList.current) {
        var nameRes = createInputRefList.current.refs.current[0].value
        var typeRes = createSelectRefList.current.refs.current[0] && createSelectRefList.current.refs.current[0].value
        
        if (!nameRes) {
          setMessage("missing name")
          setTimeout(() => setMessage(null), 2000)
          return;
        }

        if (!typeRes?.id) {
          setMessage("missing type")
          setTimeout(() => setMessage(null), 2000)
          return;
        } 

        if (createWidget) {
          createWidget({
            data: {
              name: nameRes as string,
              type: typeRes.id as number,
              placement: system ? 3 : 1,
              auto_reports: [],
              data: [],
              display_order: 0,
              page: system?.system_id ? system?.system_id : 0
            }
          });
          closeModal();
        }
      }
    }
    if (addType === "EXCISTING") {
      if (excistingSelectRefList && excistingSelectRefList.current) {
        var widgetRes = excistingSelectRefList.current.refs.current[0] && excistingSelectRefList.current.refs.current[0].value

        if (!widgetRes?.id) {
          setMessage("missing widget")
          setTimeout(() => setMessage(null), 2000)
          return;
        } 

        if (createWidget) {
          const thisWidget = widgets.find(w => w.widget_id === widgetRes?.id)
          createWidget({
            data: {
              name: thisWidget?.name as string,
              type: thisWidget?.type as number,
              placement: system ? 3 : 1,
              auto_reports: thisWidget?.auto_reports ?? [],
              data: thisWidget?.data ?? [],
              display_order: 0,
              page: system?.system_id ? system?.system_id : 0
            }
          });
          closeModal();
        }
      }
    }
  }
  // #endregion

  // Widget Type Options
  const widgetTypeOptions = [
    { label: t("widgets.widgetTypes.autoReports", {defaultValue: "Auto Reports"}), id: 1 },
    { label: t("widgets.widgetTypes.autoReportsAndMore", {defaultValue: "Auto Reports etc."}), id: 2 },
    { label: t("widgets.widgetTypes.graph", {defaultValue: "Graph"}), id: 3 },
    { label: t("widgets.widgetTypes.progress", {defaultValue: "Progress Circles"}), id: 4}
  ]
  
  // #region | Update widget
  const updatedWidgetRef = useRef<Map<number,Widget>>(new Map());

  const onChange = (widget: Widget) => {
    updatedWidgetRef.current.set(widget.widget_id, widget);
  }

  const handleUpdate = () => {
    const updatedWidget = Array.from(updatedWidgetRef.current.values());
    if (updatedWidget.length) {
      updateWidget({data: updatedWidget});
    }
  }
  // #endregion

  return (
    <NewModal
      header={t("widgets.general.widgetOptions", {defaultValue: "widget options"})}
      subHeader={system?.name ?? undefined}
      modalState={isModalOpen}
      overrides={{
        backgroundColor: currentView === "ADD" ? undefined : colors.greyLightTertiary,
        mainContentPadding: currentView === "ADD" ? MainContentPaddingTypes.secondary : MainContentPaddingTypes.primary,
        mainContentScroll: currentView === "ADD" ? false : true,
        secondaryContentScroll: true
      }}
      predefinedButtons={{ onClose: () => closeModal() }}
      tabButtons={{
        contents: [t("buttons.add", {defaultValue: "add"}), t("widgets.general.manageWidgets", {defaultValue: "manage"})],
        onClick: (i) => {
          if (i === 0) { setCurrentView("ADD") }
          if (i === 1) { setCurrentView("MANAGE") }
        },
        startEnhancers: [glyphs.Add({}), glyphs.Edit({})],
        disabled: currentView === "ADD" ? 0 : 1,
        active: currentView === "ADD" ? 0 : 1
      }}
      secondaryContent={currentView === "ADD"
        ? <div style={{ display: "flex", flexWrap: "wrap", alignItems: "start", justifyContent: "center", gap: "40px" }}>
            {/* CREATE NEW */}
            <div style={{ filter: addType === "CREATE" ? "opacity(1)" : "opacity(0.5)", transition: "filter 0.3s ease", position: "relative", width: "290px", height: "86px"}} onClick={() => setAddType("CREATE")}>
              <div style={{position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
                <NewInputList
                  ref={createInputRefList}
                  placeholders={[t("widgets.general.name", {defaultValue: "widget name"})]}
                  headers={[t("widgets.general.createNew", {defaultValue: "create new"})]}
                  isRequired={addType === "CREATE" ? [true] : [false]}
                  isClearable
                />
                <NewSelectList
                  ref={createSelectRefList}
                  placeholders={[t("widgets.general.type", {defaultValue: "widget type"})]}
                  options={[widgetTypeOptions]}
                  isRequired={addType === "CREATE" ? [true] : [false]}
                  isClearable
                />
              </div>
            </div>
            {/* COPY EXCISTING */}
            <div style={{ filter: addType === "EXCISTING" ? "opacity(1)" : "opacity(0.5)", transition: "filter 0.3s ease", position: "relative", width: "290px", height: "47px"}} onClick={() => setAddType("EXCISTING")}>
              <div style={{position: "relative", zIndex: 1}}>
                <NewSelectList
                  ref={excistingSelectRefList}
                  placeholders={[""]}
                  headers={[t("widgets.general.copyExisting", {defaultValue: "copy existing"})]}
                  options={[
                    widgets?.map((w) => ({
                      id: w.widget_id,
                      label: `${w.name}`,
                      secondaryLabel: `(${placementLabel(w.placement, w.page)})`,
                    }))
                  ]}
                isRequired={addType === "EXCISTING" ? [true] : [false]}
                isClearable
                />    
              </div>
            </div>

          </div>
      : undefined}
      mainContent={ currentView !== "ADD"
        ? widgetsToShow.length === 0 ? <p style={{...css.emptyPen, textAlign: "center"}}>{t("widgets.general.noWidgets", {defaultValue: "No widgets to show"})}</p> : <>
          <div style={{ display: "flex", justifyContent: mode === "DELETE" || mode === "EDIT"? "end" : "space-between", alignItems: "end", width: "650px", padding: "0px 20px", boxSizing: "border-box", zIndex: mode === "DELETE" ? "9999" : undefined, position: mode === "DELETE" ? "relative" : undefined }}>
            {mode === "DELETE" || mode === "EDIT" ? <></> : (
              <p style={{ ...css.penHeaders }}>
                {t("locationFeederPages.sections.penValueHeaders", { defaultValue: "Hold the mouse on a value to see column type" })}
              </p>
            )}
            {mode === "DELETE" || mode === "EDIT" ? (
              <NewButtonList
                contents={[t("buttons.save", { defaultValue: "save" }), t("buttons.cancel", { defaultValue: "cancel" }), t("buttons.edit", {defaultValue: "edit"}), t("systemControls.general.delete", { defaultValue: "delete" })]} 
                onClick={(i => {
                  if (i === 0) { mode === "DELETE" ? handleDelete() : handleUpdate(); setMode("BASIC") }
                  if (i === 1) {setMode("BASIC")}
                })}
                buttonType={ButtonTypes.tableButton}
                startEnhancers={[
                  glyphs.Check({fillColor: colors.greyDarkPrimary}),
                  glyphs.Close({ fillColor: colors.greyDarkPrimary }),
                  glyphs.Edit({fillColor: colors.greyDarkPrimary}),
                  glyphs.Delete({fillColor: colors.greyDarkPrimary})
                ]}
                disabled={[false, false, true, true, true]}
                overrides={(index) => ({
                  baseStyles: {
                    backgroundColor: (mode === "DELETE" && index === 3) || (mode === "EDIT" && index === 2)
                      ? colors.greyLightSecondary
                      : colors.greyLightPrimary,
                    opacity: (mode === "DELETE" && index === 2) || (mode === "EDIT" && index === 3)
                      ? "0.5"
                      : "1" 
                  },
                  hoverStyles: {
                    backgroundColor: colors.greyLightSecondary
                  },
                  contentStyles: {
                    color: colors.greyDarkPrimary
                  }
                })}
              />
            ) : (
              <NewButtonList
                contents={[t("buttons.edit", {defaultValue: "edit"}), t("systemControls.general.delete", { defaultValue: "delete" })]} 
                onClick={(i) => {
                  if (i === 0) { setMode("EDIT") }
                  if (i === 1) { setMode("DELETE") }
                }}
                buttonType={ButtonTypes.tableButton}
                startEnhancers={[glyphs.Edit({fillColor: colors.greyDarkPrimary}), glyphs.Delete({fillColor: colors.greyDarkPrimary})]}
                overrides={{
                  baseStyles: { backgroundColor: colors.greyLightPrimary },
                  hoverStyles: { backgroundColor: colors.greyLightSecondary },
                  contentStyles: { color: colors.greyDarkPrimary }
                }}
              />
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <WidgetList 
              widgets={widgetsToShow}
              onCheck={singleCheck}
              onChange={onChange}
              editable={mode === "EDIT"}
              deletable={mode === "DELETE"}
              selectedWidgetIds={selectedWidgetIds}
              />
          </div> 
          {mode === "DELETE" ? <Overlay overrides={{ color: colors.transparent }} beforeUnload onClickAlert /> : <></>}
          </>
          : undefined}
      footerContent={currentView === "ADD" ? 
            <NewButton 
              content={t("buttons.add", {defaultValue: "add"})}
              onClick={() => { handleCreate() }}
            /> : undefined
      }
      footerMessage={message ?? ""}
    />
    
  )
}